/**
 * The DOM HUD — everything a child or a grown-up touches (§8.1 controls per
 * state, §8.6 kid UX rules, ADDENDUM §5.3 tap gate).
 *
 * The HUD is **dumb on purpose**. It never reads the scene graph, never owns a
 * story clock and decides no policy: it renders the `PlayerUiState` the runner
 * pushes through `setState()` and sends every intention back through
 * `onTap` / `onControl` / `onPrefs`. `runner.ts#onTap` is the single tap entry
 * point and owns swallow / queue / accept and the 300 ms debounce, so there is
 * no debounce here — and nothing in this file ever answers a question by
 * itself.
 *
 * A wall clock is used here, and only here, for what §8.1 allows one for: the
 * long-press ring and the pulse animations. Nothing here advances story time.
 *
 * Sizes are **CSS pixels with floors** (§8.6). `--stage-scale` shrinks a
 * 1920-px stage to ≈ 0.42 on a landscape phone, so a control specified in stage
 * pixels would come out adult-sized. Every size is `max(design × stageScale,
 * floor)` computed in JS (`hudSizes`) and written as an inline px value, so the
 * floor holds without relying on `max()` and is observable in a test.
 *
 * **Once per frame.** The player pushes a state every frame while a story
 * plays (the seek bar and the karaoke move). So `render()` writes only what
 * changed, and the layout pass — which reads the host's size — runs only when
 * its inputs (stage rect, sizes, host size, the bar, the anchors) changed. It
 * used to run every frame, and its `clientWidth` read forced a synchronous
 * layout of the whole page each time: more than half of the player's frame
 * cost, and 135 ms per loaded texture on a slow phone.
 *
 * **A story still being made** (`stream.ts`) adds three screens, all drawn from
 * `PlayerUiState.stream`: the wait at the end of the ready parts (the moon
 * pulses, "The next part is almost ready…", Pause and Close stay), the same wait
 * when the story will only go on later (the time, and "End here") or has shown
 * nothing new for minutes (`streamStalled`: "End here" too), and "This story
 * stopped early." on the end screen of one that failed short of its parts.
 * Between parts a small "Part 2 of 7" says how far along the story being made is.
 */

import { PLAYBACK_RATES, clampRate, formatRate } from './clock'
import { ControlBar, ROTATED_ATTR, controlBarHeight } from './controls'
import type { HudPort, PlayerControl, PlayerMode, PlayerUiState, SeekRequest, TapSource } from './index'
import type { HoldKind, Phase } from './runner'
import { isOpen } from './stream'
import type { StreamInfo } from './stream'
import { THEME } from './theme'

export type { HudPort, PlayerControl, PlayerMode, PlayerUiState, SeekRequest, TapSource, HoldKind, Phase, StreamInfo }

// ---------------------------------------------------------------------------
// Sizes — CSS pixels with floors (§8.6). Pure, exported, unit-tested.
// ---------------------------------------------------------------------------

/** Design sizes in *stage* pixels, straight from the §8.6 table. */
export const HUD_DESIGN = {
  primary: 160,
  secondary: 96,
  overlay: 200,
  gate: 140,
  gap: 40,
  text: 48,
} as const

/** CSS-pixel floors. Nothing the child touches may render smaller than these. */
export const HUD_FLOOR = {
  primary: 88,
  secondary: 64,
  overlay: 96,
  gate: 88,
  gap: 24,
  text: 24,
} as const

export type HudSizes = { -readonly [K in keyof typeof HUD_DESIGN]: number }

/**
 * Peak of the ▶ pulse (`sp-hud-pulse`). It is a `transform: scale`, so at the
 * peak the button eats `primary × (PULSE_PEAK − 1) / 2` CSS pixels off each
 * side of its gap. §8.6 asks for ≥ 24 CSS px **between the things a child
 * touches**, and a measured gap is a measured gap — so the layout gap reserves
 * the bite rather than letting the animation spend it.
 */
export const PULSE_PEAK = 1.07

/**
 * `max(design × stageScale, floor)`, rounded to whole CSS pixels.
 *
 * `pulse: false` (reduced motion, §8.6) means ▶ never scales up, so the gap
 * does not have to reserve the bite and the row is not needlessly wide.
 */
export function hudSizes(stageScale: number, opts: { pulse?: boolean } = {}): HudSizes {
  const s = Number.isFinite(stageScale) && stageScale > 0 ? stageScale : 1
  const out = {} as HudSizes
  for (const key of Object.keys(HUD_DESIGN) as (keyof typeof HUD_DESIGN)[]) {
    out[key] = Math.round(Math.max(HUD_DESIGN[key] * s, HUD_FLOOR[key]))
  }
  if (opts.pulse !== false) out.gap = Math.round(out.gap + (out.primary * (PULSE_PEAK - 1)) / 2)
  return out
}

/** §8.6: "a 2 s long-press on the gear with a visible filling ring". */
export const LONG_PRESS_MS = 2000

/** A second tap on the picture inside this is a flail, not a change of mind (the runner's tap debounce). */
export const STAGE_TOGGLE_DEBOUNCE_MS = 300
/** The ▶ / ❚❚ mark a tap on the picture leaves (the app player's `flash-mark`). */
export const STAGE_FLASH_MS = 620

// ---------------------------------------------------------------------------
// Grown-up panel
// ---------------------------------------------------------------------------

/**
 * What the panel can change. `mode` covers read-to-me and bedtime (they are
 * `PlayerMode`s, not independent flags); the rest map 1:1 onto `RunnerPrefs`
 * and the audio bus.
 */
export interface HudPrefs {
  mode: PlayerMode
  questions: boolean
  /** Only this lets a `question` hold auto-answer (§8.6). */
  autoAnswer: boolean
  subtitles: boolean
  /** 0–1. */
  volume: number
  /** The M hotkey. Separate from `volume` so un-muting brings the old level back. */
  muted: boolean
  /** Playback speed, 0.5–2 (the control bar's menu, `<` / `>`, the panel's Speed row). */
  rate: number
  reducedMotion: boolean
  lowDistraction: boolean
}

export const DEFAULT_HUD_PREFS: HudPrefs = {
  mode: 'tap',
  questions: false,
  autoAnswer: false,
  subtitles: false,
  volume: 1,
  muted: false,
  rate: 1,
  reducedMotion: false,
  lowDistraction: false,
}

/** Where the stage actually is inside the host, in CSS px. */
export interface StageMetrics {
  /** `--stage-scale`: CSS px per stage px. */
  scale: number
  /** The letterboxed 16:9 picture. Portrait phones put it at the top. */
  rect: { x: number; y: number; w: number; h: number } | null
}

export interface HudOptions {
  prefs?: Partial<HudPrefs>
  /** 'preview' keeps the gear visible and hides Exit (editor use). */
  mode?: 'kid' | 'preview'
  /** Injectable wall clock (tests). */
  now?: () => number
  /** Long-press duration; §8.6's 2 s. */
  longPressMs?: number
  /** Stop reading `--stage-scale` off the host; the caller drives `setStage`. */
  measure?: boolean
}

/** `createHud`'s return: the runner's `HudPort` plus what only the mount needs. */
export interface HudHandle extends HudPort {
  readonly root: HTMLElement
  /** Grown-up-panel changes; the mount forwards them to `setPrefs` / the bus. */
  onPrefs(cb: (patch: Partial<HudPrefs>) => void): void
  /** The seek bar and the seek hotkeys (always present on this HUD). */
  onSeek(cb: (req: SeekRequest) => void): void
  /** Explicit stage geometry, when the caller knows better than the DOM. */
  setStage(metrics: Partial<StageMetrics>): void
  /** The geometry the HUD is currently sized against (shared with the subtitle band). */
  getStage(): StageMetrics
  /**
   * Stage-percent anchors for the `?` and the tap-gate hand. `bubbles.ts` owns
   * the numbers (§8.6, ADDENDUM §5.3); the HUD owns the pixels and the floor.
   */
  setAnchors(a: { question?: readonly [number, number] | null; gate?: readonly [number, number] | null }): void
  /** Reflect prefs the player owns (band defaults, restored preferences). */
  setPrefs(prefs: Partial<HudPrefs>): void
  getPrefs(): HudPrefs
  /** Full-bleed friendly failure inside the player (the mount owns the copy). */
  showError(message: string | null): void
  destroy(): void
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const STYLE_ID = 'sp-hud-style'

const HUD_CSS = `
.sp-hud, .sp-hud * { box-sizing: border-box; }
.sp-hud {
  position: absolute; inset: 0; z-index: 30;
  pointer-events: none;
  font-family: ${THEME.font};
  color: ${THEME.text};
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.sp-hud [hidden] { display: none !important; }
/* The reset is wrapped in :where() so it weighs one class, not a class plus an
   element. As ".sp-hud button" it outranked ".sp-hud-round" and its transparent
   background won, so the kids' solid next/again/start buttons rendered see-through;
   only the two-class ghost buttons escaped. Component rules below now win by order. */
.sp-hud :where(button) {
  font: inherit; color: inherit; border: 0; margin: 0; padding: 0;
  background: transparent; cursor: pointer; pointer-events: auto;
  -webkit-tap-highlight-color: transparent; touch-action: manipulation;
  display: grid; place-items: center; line-height: 1;
}
.sp-hud button:focus-visible { outline: 3px solid ${THEME.accent}; outline-offset: 3px; }
.sp-hud button[disabled] { opacity: 0.35; cursor: default; }

.sp-hud-tapzone { position: absolute; inset: 0; pointer-events: auto; background: transparent; }

/* The mark a tap on the picture leaves in its middle: the dock's own play button, drawn
   where the tap landed and fading as it grows. It never takes a pointer — what is under it
   is the picture, and the picture is the switch. */
.sp-hud-flash { position: absolute; display: grid; place-content: center; pointer-events: none; opacity: 0; }
.sp-hud-flash.is-on { animation: sp-hud-flash ${STAGE_FLASH_MS}ms ease-out forwards; }
@keyframes sp-hud-flash { from { opacity: 0.92; transform: scale(0.82); } to { opacity: 0; transform: scale(1.28); } }
.sp-hud-flash-mark { display: grid; place-items: center; aspect-ratio: 1; border-radius: 50%;
  color: ${THEME.onAccent}; background: ${THEME.accent}; box-shadow: 0 0.4rem 1.6rem rgba(4,12,10,0.45); }
.sp-hud-flash-mark svg { width: 52%; height: 52%; }
.sp-hud-flash[data-glyph="play"] .sp-hud-flash-mark svg { margin-left: 8%; }

/* The letterbox mat. The element itself is transparent and sits exactly on the
   picture; its 9999px spread shadow paints everything *outside* it, so the
   bands above/below/beside the stage are the player's own shell instead of
   black and the picture is never covered by a single pixel. Portrait phones
   show ~60 % of this mat (§8.1 puts the stage at the top) — that is the layout
   working, not a broken screen, so it has to look deliberate.

   The default is the app player's shell (a deep teal, not one of the studio's
   --color-paper-* tokens: in the light theme they are near-white, which would
   blaze beside a dark story at bedtime). A host that knows better sets
   --sp-player-mat on the player root. (No backticks in here: this comment
   lives inside a template literal.) */
.sp-hud-mat {
  position: absolute; pointer-events: none; z-index: 10;
  background: transparent;
  box-shadow: 0 0 0 9999px var(--sp-player-mat, ${THEME.shell});
  border-radius: 2px;
}
/* A soft mint spill under the picture, so the letterbox reads as a surface the
   story is resting on rather than as dead space. */
.sp-hud-glow {
  position: absolute; left: 0; right: 0; pointer-events: none; z-index: 10;
  background: linear-gradient(to bottom, rgba(146,227,196,0.08), rgba(146,227,196,0));
}
.sp-hud-mat[hidden], .sp-hud-glow[hidden] { display: none !important; }

/* The app's one round button: mint, with the glyph in deep green. */
.sp-hud-round {
  border-radius: 999px;
  background: ${THEME.accent};
  color: ${THEME.onAccent};
  border: 1px solid rgba(255,255,255,0.24);
  box-shadow: 0 0.9rem 3rem rgba(78,208,138,0.3);
  transition: transform 180ms ease, background-color 120ms ease, box-shadow 180ms ease;
}
.sp-hud-round:hover:not([disabled]) { box-shadow: 0 1.2rem 3.4rem rgba(78,208,138,0.4); }
.sp-hud-round:active { transform: scale(0.94); }
.sp-hud-round.sp-hud-ghost { background: ${THEME.glass}; color: ${THEME.text}; border-color: ${THEME.glassLine};
  box-shadow: none; backdrop-filter: blur(6px); }
.sp-hud-round.sp-hud-ghost:hover:not([disabled]) { background: rgba(6,16,18,0.72); box-shadow: none; }

.sp-hud-pause { position: absolute; }
/* Close: the one plain way out of the kid player (top-left, never behind the gear's long-press). */
.sp-hud-leave { position: absolute; }
.sp-hud-actions { position: absolute; display: flex; align-items: center; justify-content: center; }
.sp-hud-next { animation: sp-hud-pulse 1.6s ease-in-out infinite; }
.sp-hud-gate { position: absolute; animation: sp-hud-wave 1.1s ease-in-out infinite; }
@keyframes sp-hud-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.07); } }
@keyframes sp-hud-wave { 0%,100% { transform: translateY(0) rotate(-6deg); } 50% { transform: translateY(-10%) rotate(8deg); } }
@media (prefers-reduced-motion: reduce) {
  .sp-hud-next, .sp-hud-gate { animation: none; }
}
.sp-hud[data-reduced-motion="1"] .sp-hud-next,
.sp-hud[data-reduced-motion="1"] .sp-hud-gate { animation: none; }

.sp-hud-gear { position: absolute; opacity: 0.08; transition: opacity 160ms ease; }
.sp-hud-gear[data-visible="1"] { opacity: 0.4; }
.sp-hud-gear:active, .sp-hud-gear[data-pressing="1"] { opacity: 1; }
.sp-hud-ring { position: absolute; inset: 0; pointer-events: none; }
.sp-hud-ring circle { fill: none; stroke: ${THEME.accent}; stroke-linecap: round; transform: rotate(-90deg); transform-origin: 50% 50%; }

.sp-hud-sheet {
  position: absolute; inset: 0; pointer-events: auto;
  display: flex; align-items: center; justify-content: center;
  overflow-y: auto; overscroll-behavior: contain;
}
.sp-hud-panel { align-items: flex-start; }
.sp-hud-panel-card { width: min(680px, 100%); margin: auto; padding: 24px; display: flex; flex-direction: column; }
.sp-hud-panel h2 { margin: 0; font-size: 24px; font-weight: 600; }
.sp-hud-panel-hint { margin: 4px 0 0; font-size: 16px; color: ${THEME.textDim}; }
.sp-hud-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  width: 100%; padding: 12px 0; border-bottom: 1px solid ${THEME.line};
}
.sp-hud-row:last-child { border-bottom: 0; }
.sp-hud-row-label { display: flex; flex-direction: column; gap: 2px; text-align: left; }
.sp-hud-row-label b { font-weight: 600; }
.sp-hud-row-label span { color: ${THEME.textDim}; }
.sp-hud-switch { border-radius: 999px; background: ${THEME.hover}; border: 1px solid ${THEME.line}; color: ${THEME.text}; padding: 0 20px; font-weight: 600; white-space: nowrap; }
.sp-hud-switch[aria-pressed="true"] { background: ${THEME.accent}; border-color: ${THEME.accent}; color: ${THEME.onAccent}; }
.sp-hud-slider { width: 100%; accent-color: ${THEME.progress}; }
.sp-hud-scene-row { display: flex; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.sp-hud-scene-row button { border-radius: 10px; background: ${THEME.hover}; border: 1px solid ${THEME.line}; padding: 0 18px; font-weight: 600; }
.sp-hud-close { border-radius: 999px; background: ${THEME.accent}; color: ${THEME.onAccent}; font-weight: 700; padding: 0 28px; }
.sp-hud-exit { border-radius: 999px; background: rgba(211,69,63,0.9); color: #fff; font-weight: 700; padding: 0 28px; }

/* The app's opening: the picture behind a deep teal fade, and one round mint button. */
.sp-hud-start { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: auto;
  background: linear-gradient(180deg, rgba(6,16,18,0.72), rgba(5,12,14,0.92)); }
.sp-hud-start-inner { position: relative; display: flex; flex-direction: column; align-items: center; text-align: center; padding: 24px; }
.sp-hud-start-inner::before { content: ""; position: absolute; left: 50%; top: 50%; width: min(42vw, 34rem); aspect-ratio: 1;
  transform: translate(-50%, -50%); border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle, rgba(78,208,138,0.14), transparent 65%); }
.sp-hud-start-inner > * { position: relative; }
.sp-hud-start-inner p { color: ${THEME.textDim}; }
.sp-hud-bar { width: min(420px, 70vw); height: 7px; border-radius: 999px; background: ${THEME.rail}; overflow: hidden; }
.sp-hud-bar i { display: block; height: 100%; background: ${THEME.progress}; border-radius: 999px; transition: width 200ms linear; }

/* A story still being made: the wait for its next part, over the frozen last frame. The card
   itself lets taps through; only its buttons take them. */
.sp-hud-wait {
  position: absolute; display: grid; place-items: center; pointer-events: none;
  background: rgba(9,16,18,0.58); text-align: center;
}
.sp-hud-wait[data-kind="note"] { background: transparent; }
.sp-hud-wait-inner { display: flex; flex-direction: column; align-items: center; padding: 16px 24px; max-width: 100%; }
.sp-hud-wait-inner p { margin: 0; line-height: 1.25; text-shadow: 0 2px 10px rgba(0,0,0,0.6); }
.sp-hud-wait-detail { color: ${THEME.textDim}; }
.sp-hud-wait-row { display: flex; flex-wrap: wrap; justify-content: center; }
.sp-hud-pill { border-radius: 999px; background: ${THEME.accent}; color: ${THEME.onAccent}; border: 1px solid rgba(255,255,255,0.24); font-weight: 700; padding: 0 28px; }
.sp-hud-pill.sp-hud-ghost { background: ${THEME.glass}; color: ${THEME.text}; border-color: ${THEME.glassLine}; backdrop-filter: blur(6px); }
.sp-hud-moon { display: grid; place-items: center; color: ${THEME.accent}; }
.sp-hud-moon[data-pulse="1"] { animation: sp-hud-moon 2.2s ease-in-out infinite; }
@keyframes sp-hud-moon { 0%,100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.12); opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .sp-hud-moon[data-pulse="1"] { animation: none; } }
.sp-hud[data-reduced-motion="1"] .sp-hud-moon[data-pulse="1"] { animation: none; }
.sp-hud-part {
  position: absolute; transform: translateX(-50%); pointer-events: none; white-space: nowrap;
  border-radius: 999px; background: ${THEME.glass}; border: 1px solid ${THEME.glassLine}; backdrop-filter: blur(6px); padding: 4px 16px;
}

.sp-hud-dim { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: auto; background: rgba(0,0,0,0.8); }
.sp-hud-error { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: auto; background: ${THEME.shell}; padding: 24px; text-align: center; }

.sp-hud-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
`

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = HUD_CSS
  doc.head.appendChild(style)
}

// ---------------------------------------------------------------------------
// Glyphs — inline SVG so the player needs no icon font and no SPA dependency.
// ---------------------------------------------------------------------------

const svg = (body: string): string =>
  `<svg viewBox="0 0 24 24" width="58%" height="58%" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`

const GLYPH = {
  play: svg('<path d="M8 5.5 19 12 8 18.5Z" fill="currentColor" stroke-width="1.6" />'),
  pause: svg('<path d="M9 5v14M15 5v14" stroke-width="3" />'),
  again: svg('<path d="M4 12a8 8 0 1 0 2.4-5.7" /><path d="M4 4v5h5" />'),
  prev: svg('<path d="M18 5 8 12l10 7Z" fill="currentColor" stroke-width="1.6" /><path d="M6 5v14" stroke-width="3" />'),
  question: svg('<path d="M9 9a3 3 0 1 1 4 2.8c-.7.3-1 .9-1 1.7v.5" /><circle cx="12" cy="18" r="1.1" fill="currentColor" stroke="none" />'),
  hand: svg(
    '<path d="M8.5 12.5V6.2a1.6 1.6 0 0 1 3.2 0v5"/><path d="M11.7 11.2V5a1.6 1.6 0 0 1 3.2 0v6.2"/><path d="M14.9 11.6V7.4a1.6 1.6 0 0 1 3.2 0v7.1a6 6 0 0 1-6 6h-.8a5 5 0 0 1-3.8-1.8l-2.7-3.2a1.6 1.6 0 0 1 2.3-2.2l1.6 1.5"/>',
  ),
  close: svg('<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" stroke-width="2.8" />'),
  moon: svg('<path d="M19.5 14.6A8 8 0 1 1 9.4 4.5a6.4 6.4 0 0 0 10.1 10.1Z" fill="currentColor" stroke-width="1.4" />'),
  gear: svg(
    '<circle cx="12" cy="12" r="3" /><path d="M12 2.8v2.4M12 18.8v2.4M4.5 7.5l2 1.2M17.5 15.3l2 1.2M4.5 16.5l2-1.2M17.5 8.7l2-1.2" />',
  ),
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const RING_CIRCUMFERENCE = 276.46

/** The slice of `PlayerUiState` the HUD renders from. */
interface View {
  phase: Phase
  holdKind: HoldKind | null
  holdLabel: string | null
  needsGesture: boolean
  loadingActive: boolean
  loadingProgress: number
  canReplayLine: boolean
  dimmed: boolean
  sceneIndex: number
  sceneCount: number
  /** Whole-story seconds for the grown-up seek bar (`controls.ts`). */
  storyT: number
  storyDuration: number
  sceneStarts: readonly number[]
  scrubbing: boolean
  /** A story still being made (`stream.ts`); null for a hand-built state, which is complete. */
  stream: StreamInfo | null
  /** The wait has gone on with nothing new (`PlayerUiState.streamStalled`). */
  streamStalled: boolean
  /** A failed story that really stopped short (`PlayerUiState.stoppedShort`). */
  stoppedShort: boolean
}

const EMPTY_VIEW: View = {
  phase: 'paused',
  holdKind: null,
  holdLabel: null,
  needsGesture: true,
  loadingActive: true,
  loadingProgress: 0,
  canReplayLine: false,
  dimmed: false,
  sceneIndex: 0,
  sceneCount: 1,
  storyT: 0,
  storyDuration: 0,
  sceneStarts: [0],
  scrubbing: false,
  stream: null,
  streamStalled: false,
  stoppedShort: false,
}

function viewOf(s: PlayerUiState): View {
  return {
    phase: s.phase,
    holdKind: s.holdKind,
    holdLabel: s.holdLabel,
    needsGesture: s.needsGesture,
    loadingActive: s.loading?.active ?? false,
    loadingProgress: s.loading?.progress ?? 0,
    canReplayLine: s.canReplayLine,
    dimmed: s.dimmed,
    sceneIndex: s.sceneIndex,
    sceneCount: s.sceneCount,
    // A hand-built state (a test, an older player) has only the scene clock.
    storyT: s.storyT ?? s.t,
    storyDuration: s.storyDuration ?? s.sceneDuration,
    sceneStarts: s.sceneStarts ?? [0],
    scrubbing: s.scrubbing ?? false,
    stream: s.stream ?? null,
    streamStalled: s.streamStalled ?? false,
    // a hand-built state has no way to tell: a failed stream is taken as short
    stoppedShort: s.stoppedShort ?? s.stream?.state === 'failed',
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n
}

// ---------------------------------------------------------------------------
// A story still being made — the copy, pure and exported for the tests
// ---------------------------------------------------------------------------

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** `resume_at` as the local wall-clock time a grown-up reads ("15:45"), or null. */
export function formatResumeTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/**
 * What the wait at the end of the ready parts says, and whether it offers "End here": a story that
 * goes on only later, or one that has shown nothing new for minutes (`stalled`: its job may have
 * stopped without a word, and the child must not be held there for good).
 */
export function waitingCopy(stream: StreamInfo | null, stalled = false): { title: string; endHere: boolean } {
  if (stream?.state === 'paused') {
    const at = formatResumeTime(stream.resumeAt)
    return { title: at ? `The story will go on later (at ${at}).` : 'The story will go on later.', endHere: true }
  }
  if (stalled) return { title: WAIT_STALLED, endHere: true }
  return { title: 'The next part is almost ready…', endHere: false }
}

/** The wait once nothing new has come for minutes. */
export const WAIT_STALLED = 'The next part is taking a long time.'

/** The end screen of a story that failed part-way. */
export const STOPPED_EARLY = 'This story stopped early.'
/** Under the wait once a grown-up paused it: the part that arrives will not play by itself. */
export const WAIT_PAUSED = 'Paused. The next part will wait for you.'

/** "Part 2 of 7" for a story still being made, or null for a finished one. */
export function partLabel(sceneIndex: number, stream: StreamInfo | null): string | null {
  if (!stream || !isOpen(stream)) return null
  return `Part ${Math.max(1, sceneIndex + 1)} of ${stream.partsTotal}`
}

/* Write-if-different. Every render runs once per frame; a same-value write is
   still a DOM mutation (and, for `hidden` and data attributes, a style
   invalidation), so nothing is written that did not change. */
function setHidden(el: HTMLElement, hidden: boolean): void {
  if (el.hidden !== hidden) el.hidden = hidden
}
function setData(el: HTMLElement, key: string, value: string): void {
  if (el.dataset[key] !== value) el.dataset[key] = value
}
function setAttr(el: Element, name: string, value: string): void {
  if (el.getAttribute(name) !== value) el.setAttribute(name, value)
}
function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text
}

class Hud implements HudHandle {
  readonly root: HTMLElement

  private readonly doc: Document
  private readonly host: HTMLElement
  private readonly uiMode: 'kid' | 'preview'
  private readonly now: () => number
  private readonly longPressMs: number
  private readonly autoMeasure: boolean
  private readonly disposers: (() => void)[] = []

  private resizeObserver: ResizeObserver | null = null
  private observedCanvas: Element | null = null
  private view: View = { ...EMPTY_VIEW }
  private prefs: HudPrefs = { ...DEFAULT_HUD_PREFS }
  private stage: StageMetrics = { scale: 1, rect: null }
  private sizes: HudSizes = hudSizes(1)
  private panelOpen = false
  private resumeAfterPanel = false
  private errorMessage: string | null = null
  private destroyed = false
  private lastPhaseKey = ''
  /** Inputs of the last `layout()`; it runs again only when they change. */
  private lastLayoutKey = ''
  /** Inputs of the last `syncPanel()`. */
  private lastPanelKey = ''
  /** The host's CSS size, read once per measure (never per frame: the read forces a layout). */
  private hostCache: { w: number; h: number } | null = null

  // Listeners are lists, not slots: `createPlayer` registers its own
  // `onTap`/`onControl` and the mount adds one for `exit` — the second
  // subscriber must not silently replace the first.
  private readonly tapCbs: ((source: TapSource) => void)[] = []
  private readonly controlCbs: ((c: PlayerControl) => void)[] = []
  private readonly prefsCbs: ((patch: Partial<HudPrefs>) => void)[] = []
  private readonly seekCbs: ((req: SeekRequest) => void)[] = []

  /** The grown-up layer: time, seek bar, hotkeys (`controls.ts`). */
  private readonly controls: ControlBar

  private pressStart = 0
  private pressTimer: ReturnType<typeof setTimeout> | null = null
  private pressRaf: number | null = null

  private readonly tapzone: HTMLElement
  /** The ▶ / ❚❚ mark over the middle of the picture after a tap on it. */
  private readonly flash: HTMLElement
  private readonly flashMark: HTMLElement
  private flashOn: ReturnType<typeof setTimeout> | null = null
  private flashOff: ReturnType<typeof setTimeout> | null = null
  private lastStageToggle = Number.NEGATIVE_INFINITY
  private readonly mat: HTMLElement
  private readonly glow: HTMLElement
  private readonly pauseBtn: HTMLButtonElement
  private readonly actions: HTMLElement
  private readonly againBtn: HTMLButtonElement
  private readonly nextBtn: HTMLButtonElement
  private readonly questionBtn: HTMLButtonElement
  private readonly gateBtn: HTMLButtonElement
  private readonly gearBtn: HTMLButtonElement
  /** Kid mode only: leaves the player (the mount's `exit`). */
  private readonly closeBtn: HTMLButtonElement
  private readonly ringCircle: SVGCircleElement
  private readonly overlay: HTMLElement
  private readonly overlayRow: HTMLElement
  private readonly overlayBtns: HTMLButtonElement[]
  private readonly panel: HTMLElement
  private readonly panelCard: HTMLElement
  private readonly startScreen: HTMLElement
  private readonly startBtn: HTMLButtonElement
  private readonly startBar: HTMLElement
  private readonly startBarFill: HTMLElement
  private readonly startLabel: HTMLElement
  private readonly dim: HTMLElement
  private readonly dimLabel: HTMLElement
  private readonly errorScreen: HTMLElement
  private readonly errorText: HTMLElement
  /** A story still being made: the wait for its next part, or "stopped early" on the end screen. */
  private readonly waitCard: HTMLElement
  private readonly waitInner: HTMLElement
  private readonly waitRow: HTMLElement
  private readonly waitMoon: HTMLElement
  private readonly waitTitle: HTMLElement
  private readonly waitDetail: HTMLElement
  private readonly waitResumeBtn: HTMLButtonElement
  private readonly endHereBtn: HTMLButtonElement
  /** "Part 2 of 7" between the parts of a story still being made. */
  private readonly partChip: HTMLElement
  /**
   * Pause pressed during the wait. The runner keeps its `waiting` phase and holds the part that
   * arrives; the HUD is what tells the grown-up so, and offers "Keep going" instead of ❚❚.
   */
  private waitPaused = false
  private readonly live: HTMLElement
  private readonly switches = new Map<string, HTMLButtonElement>()
  private readonly ranges = new Map<'volume', HTMLInputElement>()
  private readonly rangeLabels = new Map<'volume', HTMLElement>()
  /** The panel's Speed row: one button per `PLAYBACK_RATES` entry. */
  private readonly rateBtns: HTMLButtonElement[] = []
  private panelPrev: HTMLButtonElement | null = null
  private panelSkip: HTMLButtonElement | null = null

  constructor(host: HTMLElement, opts: HudOptions = {}) {
    this.host = host
    this.doc = host.ownerDocument ?? document
    this.uiMode = opts.mode ?? 'kid'
    this.now = opts.now ?? (() => Date.now())
    this.longPressMs = opts.longPressMs ?? LONG_PRESS_MS
    this.autoMeasure = opts.measure !== false
    if (opts.prefs) this.prefs = { ...this.prefs, ...opts.prefs }
    this.prefs.rate = clampRate(this.prefs.rate)

    ensureStyle(this.doc)

    const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] => {
      const node = this.doc.createElement(tag)
      if (cls) node.className = cls
      return node
    }

    this.root = el('div', 'sp-hud')
    this.root.dataset.phase = 'paused'
    // Belt and braces for iOS: the sheet carries these too, but an inline value
    // survives a host CSP that strips the <style>.
    this.root.style.setProperty('-webkit-touch-callout', 'none')
    this.root.style.setProperty('-webkit-user-select', 'none')
    this.root.style.userSelect = 'none'
    this.root.style.touchAction = 'manipulation'

    this.live = el('div', 'sp-hud-sr')
    this.live.setAttribute('aria-live', 'polite')
    this.live.setAttribute('role', 'status')

    this.mat = el('div', 'sp-hud-mat')
    this.mat.dataset.hud = 'mat'
    this.mat.setAttribute('aria-hidden', 'true')
    this.mat.hidden = true

    this.glow = el('div', 'sp-hud-glow')
    this.glow.dataset.hud = 'glow'
    this.glow.setAttribute('aria-hidden', 'true')
    this.glow.hidden = true

    this.tapzone = el('div', 'sp-hud-tapzone')
    this.tapzone.dataset.hud = 'tapzone'
    this.tapzone.setAttribute('role', 'presentation')

    this.flash = el('div', 'sp-hud-flash')
    this.flash.dataset.hud = 'stage-flash'
    this.flash.setAttribute('aria-hidden', 'true')
    this.flashMark = el('span', 'sp-hud-flash-mark')
    this.flash.appendChild(this.flashMark)

    this.pauseBtn = this.button('pause', 'Pause the story', GLYPH.pause, 'sp-hud-pause sp-hud-round sp-hud-ghost')
    this.actions = el('div', 'sp-hud-actions')
    this.actions.dataset.hud = 'actions'
    this.againBtn = this.button('again', 'Say that again', GLYPH.again, 'sp-hud-again sp-hud-round sp-hud-ghost')
    this.nextBtn = this.button('next', 'Next', GLYPH.play, 'sp-hud-next sp-hud-round')
    this.questionBtn = this.button('question', 'A question — tap when you are ready', GLYPH.question, 'sp-hud-question sp-hud-round')
    this.gateBtn = this.button('gate', 'Tap!', GLYPH.hand, 'sp-hud-gate sp-hud-round')
    this.gearBtn = this.button('gear', 'Grown-ups: press and hold', GLYPH.gear, 'sp-hud-gear sp-hud-round sp-hud-ghost')
    this.ringCircle = this.buildRing(this.gearBtn)
    this.closeBtn = this.button('close-player', 'Close the story', GLYPH.close, 'sp-hud-leave sp-hud-round sp-hud-ghost')
    this.closeBtn.title = 'Close'

    this.overlay = el('div', 'sp-hud-sheet sp-hud-overlay')
    this.overlay.dataset.hud = 'pause-overlay'
    this.overlayRow = el('div', 'sp-hud-overlay-row')
    this.overlayRow.style.display = 'flex'
    this.overlayRow.style.alignItems = 'center'
    this.overlayBtns = [
      this.button('replay-scene', 'Watch this part again', GLYPH.again, 'sp-hud-round'),
      this.button('previous-scene', 'The part before', GLYPH.prev, 'sp-hud-round sp-hud-ghost'),
      this.button('resume', 'Keep going', GLYPH.play, 'sp-hud-round'),
    ]
    for (const b of this.overlayBtns) {
      b.hidden = false // the sheet owns their visibility
      this.overlayRow.appendChild(b)
    }
    this.overlay.appendChild(this.overlayRow)
    this.overlay.hidden = true

    this.panel = el('div', 'sp-hud-sheet sp-hud-panel')
    this.panel.dataset.hud = 'panel'
    this.panel.setAttribute('role', 'dialog')
    this.panel.setAttribute('aria-modal', 'true')
    this.panel.setAttribute('aria-label', 'Grown-up settings')
    this.panelCard = el('div', 'sp-hud-panel-card')
    this.panel.appendChild(this.panelCard)
    this.panel.hidden = true
    this.buildPanel()

    this.startScreen = el('div', 'sp-hud-start')
    this.startScreen.dataset.hud = 'start-screen'
    const startInner = el('div', 'sp-hud-start-inner')
    this.startBtn = this.button('start', 'Tap to start the story', GLYPH.play, 'sp-hud-round')
    this.startLabel = el('p')
    this.startBar = el('div', 'sp-hud-bar')
    this.startBarFill = el('i')
    this.startBar.appendChild(this.startBarFill)
    startInner.append(this.startBtn, this.startLabel, this.startBar)
    this.startScreen.appendChild(startInner)
    this.startScreen.hidden = true

    this.dim = el('div', 'sp-hud-dim')
    this.dim.dataset.hud = 'dim'
    this.dimLabel = el('span')
    this.dimLabel.textContent = 'Tap to keep going'
    this.dim.appendChild(this.dimLabel)
    this.dim.hidden = true

    this.errorScreen = el('div', 'sp-hud-error')
    this.errorScreen.dataset.hud = 'error'
    this.errorScreen.setAttribute('role', 'alert')
    this.errorText = el('p')
    this.errorText.style.margin = '0'
    this.errorScreen.appendChild(this.errorText)
    this.errorScreen.hidden = true

    this.waitCard = el('div', 'sp-hud-wait')
    this.waitCard.dataset.hud = 'waiting'
    this.waitCard.hidden = true
    this.waitInner = el('div', 'sp-hud-wait-inner')
    this.waitMoon = el('div', 'sp-hud-moon')
    this.waitMoon.dataset.hud = 'waiting-moon'
    this.waitMoon.setAttribute('aria-hidden', 'true')
    this.waitMoon.innerHTML = GLYPH.moon
    this.waitTitle = el('p')
    this.waitTitle.dataset.hud = 'waiting-text'
    this.waitDetail = el('p', 'sp-hud-wait-detail')
    this.waitDetail.dataset.hud = 'waiting-detail'
    this.waitRow = el('div', 'sp-hud-wait-row')
    this.waitResumeBtn = this.button('waiting-resume', 'Keep going', '', 'sp-hud-pill')
    this.waitResumeBtn.textContent = 'Keep going'
    this.endHereBtn = this.button('end-here', 'End the story here', '', 'sp-hud-pill sp-hud-ghost')
    this.endHereBtn.textContent = 'End here'
    this.waitRow.append(this.waitResumeBtn, this.endHereBtn)
    this.waitInner.append(this.waitMoon, this.waitTitle, this.waitDetail, this.waitRow)
    this.waitCard.appendChild(this.waitInner)

    this.partChip = el('div', 'sp-hud-part')
    this.partChip.dataset.hud = 'part-label'
    this.partChip.hidden = true

    this.controls = new ControlBar(this.doc, {
      host,
      // The kid player covers the viewport, so a key pressed with nothing
      // focused is for it; an inline preview waits until it is touched.
      engaged: this.uiMode === 'kid',
      actions: {
        seek: (req) => {
          for (const cb of [...this.seekCbs]) cb(req)
        },
        togglePlay: () => this.togglePlay(),
        scene: (delta) => this.emitControl(delta < 0 ? 'previous_scene' : 'skip_scene'),
        toggleMute: () => {
          this.changePrefs({ muted: !this.prefs.muted })
          return this.prefs.muted
        },
        toggleSubtitles: () => {
          this.changePrefs({ subtitles: !this.prefs.subtitles })
          return this.prefs.subtitles
        },
        setRate: (rate) => {
          this.changePrefs({ rate })
          return this.prefs.rate
        },
        changed: () => {
          if (!this.destroyed) this.relayout(true)
        },
      },
    })

    this.actions.append(this.againBtn, this.nextBtn, this.questionBtn)
    this.root.append(
      this.live,
      this.tapzone,
      this.flash,
      // Over the picture, under every button (pause and close stay reachable during the wait).
      this.waitCard,
      this.pauseBtn,
      this.actions,
      this.gateBtn,
      // No gear: like the app's player, the picture carries no settings button. What the
      // grown-up needs lives in the dock (speed, subtitles, full screen) and the hotkeys.
      this.overlay,
      // "Part 2 of 7" stays readable over the pictures-only overlay.
      this.partChip,
      // Above the pictures-only overlay (a paused story still shows where it
      // is), below the grown-up panel, the start screen, the dim sheet and the
      // error page, which all take the bar away.
      ...this.controls.elements,
      this.panel,
      this.startScreen,
      this.dim,
      this.errorScreen,
      // Last, so it stays reachable over the start screen and the error page.
      this.closeBtn,
    )
    // The mat and its glow live beside the HUD root, not inside it, at
    // z-index 10: above the canvas, *below* the mount's subtitle slot (20)
    // and the HUD (30). Inside the root they were painted over the portrait
    // letterbox — which is exactly where §8.6 puts the subtitle band, so on a
    // phone the words were drawn and then covered with warm brown.
    host.append(this.mat, this.glow, this.root)

    this.wire()
    this.measure()
    this.applySizes()
    this.render()
  }

  // -- construction helpers -------------------------------------------------

  private button(name: string, label: string, glyph: string, cls: string): HTMLButtonElement {
    const b = this.doc.createElement('button')
    b.type = 'button'
    b.className = cls
    b.dataset.hud = name
    b.setAttribute('aria-label', label)
    b.innerHTML = glyph
    b.hidden = true
    return b
  }

  private buildRing(gear: HTMLButtonElement): SVGCircleElement {
    const ns = 'http://www.w3.org/2000/svg'
    const node = this.doc.createElementNS(ns, 'svg')
    node.setAttribute('class', 'sp-hud-ring')
    node.setAttribute('viewBox', '0 0 100 100')
    const circle = this.doc.createElementNS(ns, 'circle')
    circle.setAttribute('cx', '50')
    circle.setAttribute('cy', '50')
    circle.setAttribute('r', '44')
    circle.setAttribute('stroke-width', '7')
    circle.setAttribute('stroke-dasharray', String(RING_CIRCUMFERENCE))
    circle.setAttribute('stroke-dashoffset', String(RING_CIRCUMFERENCE))
    node.appendChild(circle)
    gear.appendChild(node)
    return circle
  }

  private row(label: string, hint: string, control: HTMLElement): HTMLElement {
    const row = this.doc.createElement('div')
    row.className = 'sp-hud-row'
    const text = this.doc.createElement('div')
    text.className = 'sp-hud-row-label'
    const b = this.doc.createElement('b')
    b.textContent = label
    const s = this.doc.createElement('span')
    s.textContent = hint
    text.append(b, s)
    row.append(text, control)
    this.panelCard.appendChild(row)
    return row
  }

  private makeSwitch(name: string, caption: string, on: () => boolean, set: (next: boolean) => void): HTMLButtonElement {
    const b = this.doc.createElement('button')
    b.type = 'button'
    b.className = 'sp-hud-switch'
    b.dataset.hud = `switch-${name}`
    b.textContent = caption
    b.setAttribute('aria-pressed', 'false')
    b.addEventListener('click', (e) => {
      e.stopPropagation()
      set(!on())
    })
    this.switches.set(name, b)
    return b
  }

  /** The panel's Speed row: the same six speeds as the control bar's menu. */
  private makeRateRow(): HTMLElement {
    const row = this.doc.createElement('div')
    row.className = 'sp-hud-scene-row'
    row.dataset.hud = 'panel-speed'
    row.setAttribute('role', 'radiogroup')
    row.setAttribute('aria-label', 'Speed')
    for (const r of PLAYBACK_RATES) {
      const b = this.doc.createElement('button')
      b.type = 'button'
      b.dataset.hud = 'panel-speed-option'
      b.dataset.rate = String(r)
      b.setAttribute('role', 'radio')
      b.setAttribute('aria-checked', 'false')
      b.textContent = formatRate(r)
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        this.changePrefs({ rate: r })
      })
      this.rateBtns.push(b)
      row.appendChild(b)
    }
    return row
  }

  private makeRange(key: 'volume', min: number, max: number, step: number): HTMLElement {
    const wrap = this.doc.createElement('div')
    wrap.style.display = 'flex'
    wrap.style.flexDirection = 'column'
    wrap.style.alignItems = 'flex-end'
    wrap.style.minWidth = '180px'
    const input = this.doc.createElement('input')
    input.type = 'range'
    input.className = 'sp-hud-slider'
    input.dataset.hud = `range-${key}`
    input.min = String(min)
    input.max = String(max)
    input.step = String(step)
    input.value = String(this.prefs[key])
    const value = this.doc.createElement('span')
    value.style.color = THEME.textDim
    const onInput = (): void => {
      const n = Number(input.value)
      this.changePrefs({ [key]: Number.isFinite(n) ? n : this.prefs[key] } as Partial<HudPrefs>)
    }
    input.addEventListener('input', onInput)
    input.addEventListener('change', onInput)
    wrap.append(input, value)
    this.ranges.set(key, input)
    this.rangeLabels.set(key, value)
    return wrap
  }

  private buildPanel(): void {
    const head = this.doc.createElement('div')
    const h = this.doc.createElement('h2')
    h.textContent = 'Grown-up settings'
    const hint = this.doc.createElement('p')
    hint.className = 'sp-hud-panel-hint'
    hint.textContent = 'The story is paused while this is open.'
    head.append(h, hint)
    this.panelCard.appendChild(head)

    this.row(
      'Read to me',
      'The story keeps going on its own.',
      this.makeSwitch(
        'read_to_me',
        'Autoplay',
        () => this.prefs.mode === 'read_to_me',
        (next) => this.changePrefs({ mode: next ? 'read_to_me' : 'tap' }),
      ),
    )
    this.row(
      'Bedtime',
      'Longer pauses, no questions.',
      this.makeSwitch(
        'bedtime',
        'Bedtime',
        () => this.prefs.mode === 'bedtime',
        (next) => this.changePrefs({ mode: next ? 'bedtime' : 'tap' }),
      ),
    )
    this.row(
      'Questions',
      'Ask the child a question now and then.',
      this.makeSwitch('questions', 'Questions', () => this.prefs.questions, (next) => this.changePrefs({ questions: next })),
    )
    this.row(
      'Answer for them',
      'After a few seconds a question answers itself.',
      this.makeSwitch('autoAnswer', 'Auto-answer', () => this.prefs.autoAnswer, (next) => this.changePrefs({ autoAnswer: next })),
    )
    this.row(
      'Subtitles',
      'Show the words under the picture.',
      this.makeSwitch('subtitles', 'Subtitles', () => this.prefs.subtitles, (next) => this.changePrefs({ subtitles: next })),
    )
    this.row('Volume', '', this.makeRange('volume', 0, 1, 0.05))
    this.row('Speed', 'The whole story: voices, pictures and words.', this.makeRateRow())
    this.row(
      'Less movement',
      'Cuts instead of camera moves.',
      this.makeSwitch(
        'reducedMotion',
        'Reduced motion',
        () => this.prefs.reducedMotion,
        (next) => this.changePrefs({ reducedMotion: next }),
      ),
    )

    this.row(
      'Low distraction',
      'Fewer decorative effects. Actions and teaching highlights stay visible.',
      this.makeSwitch('lowDistraction', 'Low distraction', () => this.prefs.lowDistraction, (next) => this.changePrefs({ lowDistraction: next })),
    )

    const sceneRow = this.doc.createElement('div')
    sceneRow.className = 'sp-hud-scene-row'
    const sceneBtn = (name: string, label: string, control: PlayerControl): HTMLButtonElement => {
      const b = this.doc.createElement('button')
      b.type = 'button'
      b.dataset.hud = name
      b.textContent = label
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        this.emitControl(control)
        this.closePanel()
      })
      return b
    }
    this.panelPrev = sceneBtn('panel-previous-scene', '⏮ Previous part', 'previous_scene')
    this.panelSkip = sceneBtn('panel-skip-scene', '⏭ Skip this part', 'skip_scene')
    sceneRow.append(this.panelPrev, sceneBtn('panel-replay-scene', '↺ This part again', 'replay_scene'), this.panelSkip)
    this.row('This part', '', sceneRow)

    const footer = this.doc.createElement('div')
    footer.className = 'sp-hud-row'
    const exit = this.doc.createElement('button')
    exit.type = 'button'
    exit.className = 'sp-hud-exit'
    exit.dataset.hud = 'panel-exit'
    exit.textContent = 'Exit'
    exit.hidden = this.uiMode === 'preview'
    exit.addEventListener('click', (e) => {
      e.stopPropagation()
      this.panelOpen = false
      this.resumeAfterPanel = false
      this.emitControl('exit')
      this.render()
    })
    const restart = this.doc.createElement('button')
    restart.type = 'button'
    restart.className = 'sp-hud-close'
    restart.dataset.hud = 'panel-restart'
    restart.textContent = 'From the start'
    restart.addEventListener('click', (e) => {
      e.stopPropagation()
      this.emitControl('restart')
      this.closePanel()
    })
    const close = this.doc.createElement('button')
    close.type = 'button'
    close.className = 'sp-hud-close'
    close.dataset.hud = 'panel-close'
    close.textContent = 'Back to the story'
    close.addEventListener('click', (e) => {
      e.stopPropagation()
      this.closePanel()
    })
    footer.append(exit, restart, close)
    this.panelCard.appendChild(footer)
  }

  // -- wiring ---------------------------------------------------------------

  private listen(target: EventTarget, type: string, handler: (e: Event) => void): void {
    target.addEventListener(type, handler)
    this.disposers.push(() => target.removeEventListener(type, handler))
  }

  private onClick(btn: HTMLElement, run: () => void): void {
    this.listen(btn, 'click', (e) => {
      e.stopPropagation()
      e.preventDefault()
      run()
    })
  }

  private emitTap(source: TapSource): void {
    for (const cb of [...this.tapCbs]) cb(source)
  }

  private emitControl(c: PlayerControl): void {
    for (const cb of [...this.controlCbs]) cb(c)
  }

  /**
   * Space / K. "Play/pause" means something different in each phase, and each
   * meaning already has a route, so this only picks one: pause while a line
   * plays, the overlay's ▶ while paused, ▶ *continue* (the one tap entry
   * point) at a hold, ▶ *again* at the end, *start* on the start screen —
   * and, waiting for the next part, pause the wait or take the pause back.
   */
  private togglePlay(): void {
    const v = this.view
    if (v.needsGesture || v.loadingActive) {
      if (!v.loadingActive) this.emitTap('hud')
      return
    }
    switch (v.phase) {
      case 'playing':
        this.emitControl('pause')
        return
      case 'holding':
      case 'question':
        this.emitTap('hud')
        return
      case 'paused':
      case 'ended':
        this.emitControl('play')
        return
      case 'waiting':
        if (this.waitPaused) this.resumeWait()
        else this.pausePressed()
        return
      default:
        return
    }
  }

  /** The dock can show (the story has a length to seek through), so its play/pause is the one. */
  private dockOwnsPause(): boolean {
    const T = this.view.storyDuration
    return Number.isFinite(T) && T > 0
  }

  /**
   * A tap on the picture. Wherever the dock can show it is the dock's play/pause, as in the
   * app's player, and it leaves the same mark in the middle of the picture. A hold still takes
   * it as *continue* (a tap gate is a tap on the picture), and a story with no length yet keeps
   * the runner's tap policy.
   */
  private stageTap(): void {
    const v = this.view
    if (!this.dockOwnsPause() || v.phase === 'holding' || v.phase === 'question') {
      this.emitTap('stage')
      return
    }
    const now = this.now()
    if (now - this.lastStageToggle < STAGE_TOGGLE_DEBOUNCE_MS) return
    this.lastStageToggle = now
    // Which glyph, decided before the toggle: what the story was doing when the finger landed.
    const running = v.phase === 'playing' || (v.phase === 'waiting' && !this.waitPaused)
    this.showFlash(running ? 'pause' : 'play')
    this.togglePlay()
  }

  /**
   * Replays the mark. The class comes off and goes back on a task later: removed and re-added
   * in one go it would not restart, and a second tap inside the first mark's life would leave
   * it stuck mid-fade.
   */
  private showFlash(glyph: 'play' | 'pause'): void {
    this.flash.classList.remove('is-on')
    if (this.flash.dataset.glyph !== glyph) {
      this.flash.dataset.glyph = glyph
      this.flashMark.innerHTML = GLYPH[glyph]
    }
    if (this.flashOn !== null) clearTimeout(this.flashOn)
    if (this.flashOff !== null) clearTimeout(this.flashOff)
    this.flashOn = setTimeout(() => {
      this.flashOn = null
      this.flash.classList.add('is-on')
    }, 0)
    this.flashOff = setTimeout(() => {
      this.flashOff = null
      this.flash.classList.remove('is-on')
    }, STAGE_FLASH_MS + 20)
  }

  /** ❚❚. During the wait it is remembered here too: the runner's phase stays `waiting`. */
  private pausePressed(): void {
    if (this.view.phase === 'waiting') this.waitPaused = true
    this.emitControl('pause')
    this.render()
  }

  /** "Keep going" under a paused wait: the next part plays by itself again when it arrives. */
  private resumeWait(): void {
    this.waitPaused = false
    this.emitControl('play')
    this.render()
  }

  private wire(): void {
    // iOS callout / selection / magnifier — off everywhere in the HUD.
    this.listen(this.root, 'contextmenu', (e) => e.preventDefault())
    this.listen(this.root, 'selectstart', (e) => e.preventDefault())
    this.listen(this.root, 'dragstart', (e) => e.preventDefault())

    this.listen(this.tapzone, 'pointerdown', (e) => {
      e.preventDefault()
      // A finger on the picture while the dock is hidden only brings the dock back (the host's
      // own pointerdown shows it), as a phone's video player does: on an iPhone the first tap
      // on a playing story paused it, and the button the parent then pressed played it again.
      // A mouse click, a story not started yet and a tap-to-continue keep their meaning.
      const v = this.view
      const toggles = this.dockOwnsPause() && !v.needsGesture && !v.loadingActive && v.phase !== 'holding' && v.phase !== 'question'
      if (toggles && (e as PointerEvent).pointerType !== 'mouse' && !this.controls.isShown) return
      this.stageTap()
    })
    this.listen(this.dim, 'pointerdown', (e) => {
      e.preventDefault()
      this.emitTap('stage')
    })

    this.onClick(this.pauseBtn, () => this.pausePressed())
    this.onClick(this.closeBtn, () => this.emitControl('exit'))
    this.onClick(this.waitResumeBtn, () => this.resumeWait())
    this.onClick(this.endHereBtn, () => this.emitControl('end_stream'))
    // ▶ / ? / the hand are all *continue* — the one tap entry point (§8.1).
    this.onClick(this.nextBtn, () => this.emitTap('hud'))
    this.onClick(this.questionBtn, () => this.emitTap('hud'))
    this.onClick(this.gateBtn, () => this.emitTap('hud'))
    this.onClick(this.startBtn, () => this.emitTap('hud'))
    this.onClick(this.againBtn, () => this.emitControl('again'))

    const [replayScene, prevScene, resume] = this.overlayBtns
    if (replayScene) this.onClick(replayScene, () => this.emitControl('replay_scene'))
    if (prevScene) this.onClick(prevScene, () => this.emitControl('previous_scene'))
    if (resume) this.onClick(resume, () => this.emitControl('play'))

    // The gear: a 2 s long-press with a filling ring. A plain click does
    // nothing at all — that is the whole point (§8.6).
    this.listen(this.gearBtn, 'pointerdown', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.beginPress()
    })
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.listen(this.gearBtn, type, () => this.cancelPress())
    }
    this.listen(this.gearBtn, 'click', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })
    // Keyboard escape hatch for a grown-up on a laptop.
    this.listen(this.gearBtn, 'keydown', (e) => {
      const key = (e as KeyboardEvent).key
      if (key === 'Enter' || key === ' ') {
        e.preventDefault()
        this.openPanel()
      }
    })
    this.listen(this.panel, 'keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') this.closePanel()
    })

    const win = this.doc.defaultView
    if (win && this.autoMeasure) {
      const onResize = (): void => {
        this.measure()
        this.applySizes()
        this.render()
      }
      this.listen(win, 'resize', onResize)
      this.listen(win, 'orientationchange', onResize)
    }
  }

  private beginPress(): void {
    this.cancelPress()
    this.pressStart = this.now()
    this.gearBtn.dataset.pressing = '1'
    this.pressTimer = setTimeout(() => {
      this.pressTimer = null
      this.setRing(1)
      this.openPanel()
    }, this.longPressMs)
    const tick = (): void => {
      if (this.pressTimer === null) return
      const p = clamp((this.now() - this.pressStart) / this.longPressMs, 0, 1)
      this.setRing(p)
      if (p < 1) this.pressRaf = this.raf(tick)
    }
    this.pressRaf = this.raf(tick)
  }

  private cancelPress(): void {
    if (this.pressTimer !== null) {
      clearTimeout(this.pressTimer)
      this.pressTimer = null
    }
    if (this.pressRaf !== null) {
      this.cancelRaf(this.pressRaf)
      this.pressRaf = null
    }
    delete this.gearBtn.dataset.pressing
    this.setRing(0)
  }

  private raf(cb: () => void): number | null {
    const w = this.doc.defaultView
    if (!w || typeof w.requestAnimationFrame !== 'function') return null
    return w.requestAnimationFrame(cb)
  }

  private cancelRaf(id: number): void {
    const w = this.doc.defaultView
    if (w && typeof w.cancelAnimationFrame === 'function') w.cancelAnimationFrame(id)
  }

  private setRing(p: number): void {
    this.gearBtn.style.setProperty('--hud-ring', String(Math.round(p * 100)))
    this.ringCircle.setAttribute('stroke-dashoffset', String((1 - p) * RING_CIRCUMFERENCE))
  }

  // -- panel ----------------------------------------------------------------

  private openPanel(): void {
    this.cancelPress()
    if (this.panelOpen) return
    this.panelOpen = true
    this.resumeAfterPanel = this.view.phase === 'playing'
    if (this.resumeAfterPanel) this.emitControl('pause')
    this.render()
    const first = this.switches.get('read_to_me')
    if (first && typeof first.focus === 'function') first.focus()
  }

  private closePanel(): void {
    if (!this.panelOpen) return
    this.panelOpen = false
    if (this.resumeAfterPanel) {
      this.resumeAfterPanel = false
      this.emitControl('play')
    }
    this.render()
  }

  private changePrefs(patch: Partial<HudPrefs>): void {
    const next: Partial<HudPrefs> = { ...patch }
    // Bedtime forces questions off (§8.6). The runner enforces the 2 s hold;
    // the HUD makes the coupling visible so a grown-up is not left wondering
    // why the questions switch did nothing.
    if (next.mode === 'bedtime') {
      next.questions = false
      next.autoAnswer = false
    }
    if (next.questions === false) next.autoAnswer = false
    if (next.autoAnswer === true) next.questions = true
    if (next.rate !== undefined) next.rate = clampRate(next.rate)
    this.prefs = { ...this.prefs, ...next }
    this.syncPanel()
    for (const cb of [...this.prefsCbs]) cb(next)
    // The bar's speed label and the panel reflect the change before the player's next push.
    if (next.rate !== undefined) this.render()
  }

  private panelKey(): string {
    const p = this.prefs
    return `${p.mode}|${p.questions}|${p.autoAnswer}|${p.subtitles}|${p.reducedMotion}|${p.lowDistraction}|${p.volume}|${p.rate}`
  }

  private syncPanel(): void {
    const key = this.panelKey()
    if (key === this.lastPanelKey) return
    this.lastPanelKey = key
    const on: Record<string, boolean> = {
      read_to_me: this.prefs.mode === 'read_to_me',
      bedtime: this.prefs.mode === 'bedtime',
      questions: this.prefs.questions,
      autoAnswer: this.prefs.autoAnswer,
      subtitles: this.prefs.subtitles,
      reducedMotion: this.prefs.reducedMotion,
      lowDistraction: this.prefs.lowDistraction,
    }
    for (const [name, btn] of this.switches) btn.setAttribute('aria-pressed', on[name] ? 'true' : 'false')
    const autoAnswer = this.switches.get('autoAnswer')
    if (autoAnswer) autoAnswer.disabled = !this.prefs.questions
    const questions = this.switches.get('questions')
    if (questions) questions.disabled = this.prefs.mode === 'bedtime'
    for (const [key, input] of this.ranges) {
      const v = this.prefs[key]
      if (input.value !== String(v)) input.value = String(v)
      const label = this.rangeLabels.get(key)
      if (label) label.textContent = `${Math.round(v * 100)}%`
    }
    const rate = clampRate(this.prefs.rate)
    for (const b of this.rateBtns) {
      const checked = Math.abs(Number(b.dataset.rate) - rate) < 1e-6
      b.setAttribute('aria-checked', checked ? 'true' : 'false')
      b.style.background = checked ? THEME.accent : ''
      b.style.color = checked ? THEME.onAccent : ''
    }
    this.root.dataset.reducedMotion = this.prefs.reducedMotion ? '1' : '0'
  }

  // -- sizing + layout ------------------------------------------------------

  /**
   * Read `--stage-scale` and the canvas box off the host. `stage.ts` owns both;
   * the HUD does not want a second source of truth, but it also must not
   * depend on the renderer calling it.
   */
  private measure(): void {
    if (!this.autoMeasure) return
    this.hostCache = null
    const win = this.doc.defaultView
    if (!win || typeof win.getComputedStyle !== 'function') return
    let scale = Number.NaN
    try {
      scale = Number.parseFloat(win.getComputedStyle(this.host).getPropertyValue('--stage-scale'))
    } catch {
      /* jsdom without a layout engine */
    }
    if (Number.isFinite(scale) && scale > 0) this.stage.scale = scale
    const canvas = this.host.querySelector('canvas')
    if (canvas !== this.observedCanvas) this.observeCanvas(canvas)
    if (canvas && typeof canvas.getBoundingClientRect === 'function') {
      const hostRect = this.host.getBoundingClientRect()
      const r = canvas.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) {
        // Drawn sideways (the window-filling full screen on an upright phone, `controls.ts`): the
        // host is turned a quarter clockwise, so its x runs down the screen and its y runs from
        // the screen's right edge leftwards. The layout is the host's, in its own axes.
        this.stage.rect =
          this.host.getAttribute(ROTATED_ATTR) === '1'
            ? { x: r.top - hostRect.top, y: hostRect.right - r.right, w: r.height, h: r.width }
            : { x: r.left - hostRect.left, y: r.top - hostRect.top, w: r.width, h: r.height }
      }
    }
    this.sizes = hudSizes(this.stage.scale, { pulse: !this.prefs.reducedMotion })
  }

  /**
   * The canvas is created by `stage.ts` *after* the HUD mounts and settles its
   * letterbox a frame later, so a one-shot measurement in the constructor sees
   * a stale `--stage-scale`. A `ResizeObserver` on the canvas is the only cheap
   * way to learn the truth: re-measuring on every `setState` would force a
   * layout once per rAF tick.
   */
  private observeCanvas(canvas: Element | null): void {
    if (!this.resizeObserver) {
      const win = this.doc.defaultView
      const Ctor = win && 'ResizeObserver' in win ? win.ResizeObserver : null
      if (!Ctor) {
        this.observedCanvas = canvas
        return
      }
      this.resizeObserver = new Ctor(() => this.remeasure())
      this.disposers.push(() => this.resizeObserver?.disconnect())
      this.resizeObserver.observe(this.host)
    }
    if (this.observedCanvas) this.resizeObserver.unobserve(this.observedCanvas)
    this.observedCanvas = canvas
    if (canvas) this.resizeObserver.observe(canvas)
  }

  private remeasure(): void {
    if (this.destroyed) return
    this.measure()
    this.applySizes()
    this.render()
  }

  /**
   * The host's size in CSS px. Cached: `clientWidth` forces a synchronous
   * layout, and this used to be read on every frame. `measure()` (resize, the
   * ResizeObserver, a phase change) and `setStage()` drop the cache.
   */
  private hostSize(): { w: number; h: number } {
    if (this.hostCache) return this.hostCache
    const rect = this.stage.rect
    const parent = this.root.parentElement
    const w = this.root.clientWidth || parent?.clientWidth || (rect ? rect.x * 2 + rect.w : 0)
    const h = this.root.clientHeight || parent?.clientHeight || (rect ? rect.y * 2 + rect.h : 0)
    this.hostCache = { w, h }
    return this.hostCache
  }

  /** Everything `layout()` reads. Equal keys lay out equally, so the pass is skipped. */
  private layoutKey(available: boolean): string {
    const s = this.sizes
    const r = this.stage.rect
    const { w, h } = this.hostSize()
    const pt = (p: readonly [number, number] | null): string => (p ? `${p[0]},${p[1]}` : '-')
    return [
      s.primary,
      s.gap,
      s.gate,
      s.text,
      r ? `${r.x},${r.y},${r.w},${r.h}` : '-',
      `${w}x${h}`,
      this.controls.isShown ? 1 : 0,
      available ? 1 : 0,
      pt(this.gatePoint),
      pt(this.questionPoint),
    ].join('|')
  }

  /** Lay out when an input changed (or when forced: the bar showed or hid itself). */
  private relayout(force = false, available = this.lastAvailable): void {
    const key = this.layoutKey(available)
    if (!force && key === this.lastLayoutKey) return
    this.lastLayoutKey = key
    this.layout()
  }

  /** The control bar's `available` flag of the last render (an input of the layout). */
  private lastAvailable = false

  private applySizes(): void {
    const s = this.sizes
    const px = (n: number): string => `${n}px`
    const square = (el: HTMLElement, n: number): void => {
      el.style.width = px(n)
      el.style.height = px(n)
      el.style.minWidth = px(n)
      el.style.minHeight = px(n)
      el.style.fontSize = px(Math.round(n * 0.4))
    }

    for (const b of [this.nextBtn, this.againBtn, this.questionBtn, this.startBtn, this.pauseBtn]) square(b, s.primary)
    square(this.gateBtn, s.gate)
    square(this.gearBtn, s.secondary)
    square(this.closeBtn, s.secondary)
    for (const b of this.overlayBtns) square(b, s.overlay)

    this.actions.style.gap = px(s.gap)
    this.overlayRow.style.gap = px(s.gap)

    // The panel is grown-up UI, but a grown-up on a phone still needs a target
    // a thumb can hit: the same floors apply, only the shape differs.
    for (const b of this.switches.values()) {
      b.style.minHeight = px(HUD_FLOOR.primary)
      b.style.minWidth = px(HUD_FLOOR.primary)
      b.style.fontSize = px(HUD_FLOOR.text)
    }
    for (const b of this.panelCard.querySelectorAll<HTMLElement>('.sp-hud-scene-row button, .sp-hud-close, .sp-hud-exit')) {
      b.style.minHeight = px(HUD_FLOOR.primary)
      b.style.fontSize = px(HUD_FLOOR.text)
    }
    for (const input of this.ranges.values()) input.style.height = px(HUD_FLOOR.primary)
    for (const row of this.panelCard.querySelectorAll<HTMLElement>('.sp-hud-row')) row.style.gap = px(HUD_FLOOR.gap)
    this.panelCard.style.gap = px(HUD_FLOOR.gap)

    this.startLabel.style.fontSize = px(s.text)
    this.startLabel.style.margin = `${px(HUD_FLOOR.gap)} 0`
    this.errorText.style.fontSize = px(s.text)
    this.dimLabel.style.fontSize = px(s.text)

    // The wait for the next part: the moon at the overlay buttons' size, words at the text floor,
    // and its two buttons as tall as anything else a child can touch.
    const small = px(Math.max(HUD_FLOOR.text, Math.round(s.text * 0.75)))
    square(this.waitMoon, s.overlay)
    this.waitTitle.style.fontSize = px(s.text)
    this.waitDetail.style.fontSize = small
    this.waitInner.style.gap = px(HUD_FLOOR.gap)
    this.waitRow.style.gap = px(s.gap)
    for (const b of [this.waitResumeBtn, this.endHereBtn]) {
      b.style.minHeight = px(s.primary)
      b.style.fontSize = small
    }
    this.partChip.style.fontSize = small
  }

  /**
   * Portrait phones put the stage at the top; the controls then live in the
   * free space **below** it and never cover the picture (§8.6 Orientation).
   * When there is no free band they sit inside the stage's bottom edge.
   */
  private layout(): void {
    const s = this.sizes
    const rect = this.stage.rect
    const { w: hostW, h: hostH } = this.hostSize()
    const stageBottom = rect ? rect.y + rect.h : hostH
    const letterbox = Math.max(0, hostH - stageBottom)

    // The grown-up bar (controls.ts). In a letterbox tall enough to keep the
    // kid buttons in a row of their own it sits directly under the picture,
    // like a phone video player; otherwise it lies across the picture's bottom
    // edge and everything down there moves up while it is showing.
    const barMode = letterbox >= controlBarHeight(rect?.w ?? hostW, 'below') + s.primary + s.gap * 2 ? 'below' : 'over'
    this.controls.place({
      mode: barMode,
      left: rect?.x ?? 0,
      width: rect?.w ?? hostW,
      stageTop: rect?.y ?? 0,
      stageBottom,
      hostHeight: hostH,
      textPx: Math.max(HUD_FLOOR.text, Math.round(s.text * 0.75)),
    })
    const barOver = this.controls.overInset
    const barBelow = this.controls.belowInset
    // The subtitle slot is the mount's, not ours; it reads these two.
    this.host.style.setProperty('--sp-ctl-over', `${barOver}px`)
    this.host.style.setProperty('--sp-ctl-below', `${barBelow}px`)

    const band = Math.max(0, letterbox - barBelow)
    const below = band >= s.primary + s.gap

    // In the letterbox the buttons sit at its **bottom**, under the thumbs,
    // and the subtitle band takes the top of it directly under the picture —
    // centring both would stack them on top of each other on a portrait phone.
    const top = below ? hostH - s.gap - s.primary : stageBottom - barOver - s.gap - s.primary
    this.actions.style.left = '0px'
    this.actions.style.right = '0px'
    this.actions.style.top = `${Math.round(top)}px`
    this.actions.style.height = `${s.primary}px`
    this.actions.dataset.placement = below ? 'below' : 'over'

    // Pause sits in the stage's top-right corner — out of the reading path and
    // the only control visible while a line plays. On a portrait phone that
    // corner is at the very top of a 844 px screen, where no thumb reaches, so
    // a tall letterbox brings it down into its own row above ▶/↺ (still ≥ 24 px
    // clear of them, still on the side away from the words).
    const pad = Math.round(s.gap / 2)
    const tallBand = below && band >= s.primary * 2 + s.gap * 3
    if (tallBand) {
      this.pauseBtn.style.top = `${Math.round(top - s.gap - s.primary)}px`
    } else {
      this.pauseBtn.style.top = `${Math.round((rect?.y ?? 0) + pad)}px`
    }
    this.pauseBtn.style.right = `${pad}px`

    // The gear hides in the opposite corner, as far from ▶ as the stage allows
    // — and above the grown-up bar while that is across the picture, or its
    // long-press would land on the seek bar.
    const gearBottom = barOver > 0 ? letterbox + barOver + pad : pad
    this.gearBtn.style.bottom = `${Math.round(gearBottom)}px`
    this.gearBtn.style.left = `${pad}px`

    // Close takes the top-left corner, opposite pause.
    this.closeBtn.style.top = `${Math.round((rect?.y ?? 0) + pad)}px`
    this.closeBtn.style.left = `${pad}px`

    // A story still being made: the wait lies over the picture (never the letterbox, where the
    // subtitle band and the buttons live), and "Part 2 of 7" sits between close and pause.
    const pic = rect ?? { x: 0, y: 0, w: hostW, h: stageBottom }
    this.waitCard.style.left = `${Math.round(pic.x)}px`
    this.waitCard.style.top = `${Math.round(pic.y)}px`
    this.waitCard.style.width = `${Math.round(pic.w)}px`
    this.waitCard.style.height = `${Math.round(Math.max(0, pic.h - barOver))}px`
    this.partChip.style.left = `${Math.round(pic.x + pic.w / 2)}px`
    this.partChip.style.top = `${Math.round(pic.y + pad)}px`
    // The tap mark sits in the middle of the picture, at the app's size: 15 % of its short side.
    this.flash.style.left = `${Math.round(pic.x)}px`
    this.flash.style.top = `${Math.round(pic.y)}px`
    this.flash.style.width = `${Math.round(pic.w)}px`
    this.flash.style.height = `${Math.round(pic.h)}px`
    this.flashMark.style.width = `${Math.round(clamp(Math.min(pic.w, pic.h) * 0.15, 48, 86))}px`

    this.layoutMat(rect, letterbox)

    this.placeGate(barOver > 0 ? stageBottom - barOver : null)
  }

  /**
   * §8.1 letterboxes a 16:9 stage into the host and pins it to the **top** in
   * portrait, which on a 390×844 phone leaves roughly 60 % of the screen with
   * no picture in it. That is the specified layout and the right one (no
   * orientation dead end, subtitles never over the picture) — but a black void
   * reads as a broken player, so the free space is painted warm paper from the
   * theme instead. The mat covers only what is *outside* the stage rect.
   */
  private layoutMat(rect: StageMetrics['rect'], band: number): void {
    if (!rect || rect.w <= 0 || rect.h <= 0) {
      this.mat.hidden = true
      this.glow.hidden = true
      return
    }
    this.mat.hidden = false
    this.mat.style.left = `${Math.round(rect.x)}px`
    this.mat.style.top = `${Math.round(rect.y)}px`
    this.mat.style.width = `${Math.round(rect.w)}px`
    this.mat.style.height = `${Math.round(rect.h)}px`

    const glow = Math.min(band, Math.round(rect.h * 0.35))
    this.glow.hidden = glow < 24
    this.glow.style.top = `${Math.round(rect.y + rect.h)}px`
    this.glow.style.height = `${Math.max(0, glow)}px`
  }

  /**
   * The hand glyph and the `?` are DOM (so they obey the floor) but their
   * *position* comes from the stage: `world.ts` computes it from the speaking
   * actor's `head_top` through `bubbles.ts#stageAnchorsFor` and `index.ts`
   * pushes it here with `setAnchors` whenever it moves. With no anchor (no
   * renderer, nobody speaking yet) they fall back to the action row, which is
   * correct at every width — just less expressive.
   */
  private placeGate(barTop: number | null = null): void {
    const rect = this.stage.rect
    const { w: hostW, h: hostH } = this.hostSize()
    // Nothing a child taps may sit under the grown-up bar while it shows.
    const floor = barTop === null ? hostH : barTop
    for (const [el, size, pct] of [
      [this.gateBtn, this.sizes.gate, this.gatePoint],
      [this.questionBtn, this.sizes.primary, this.questionPoint],
    ] as const) {
      if (!pct || !rect) {
        if (el === this.questionBtn && el.parentElement !== this.actions) this.actions.appendChild(el)
        if (el === this.gateBtn) {
          el.style.left = ''
          el.style.top = ''
          el.style.bottom = `${Math.round(this.sizes.gap + (hostH - floor))}px`
          el.style.right = `${Math.round((hostW - this.sizes.gate) / 2)}px`
        }
        continue
      }
      if (el.parentElement !== this.root) this.root.appendChild(el)
      const x = rect.x + (pct[0] / 100) * rect.w
      const y = rect.y + (pct[1] / 100) * rect.h
      el.style.bottom = ''
      el.style.right = ''
      el.style.left = `${Math.round(clamp(x - size / 2, 0, Math.max(0, hostW - size)))}px`
      el.style.top = `${Math.round(clamp(y - size / 2, 0, Math.max(0, floor - size)))}px`
    }
  }

  /** Stage-percent anchors, when `bubbles.ts` supplies them (§8.6). */
  private gatePoint: readonly [number, number] | null = null
  private questionPoint: readonly [number, number] | null = null

  // -- render ---------------------------------------------------------------

  /**
   * The wait for the next part (moon, words, "Keep going" once paused, "End here" when the story
   * only goes on later or has stalled), "This story stopped early." on the end screen of one that
   * failed short of its parts, and "Part 2 of 7" where the story stands still between parts (`showPart`).
   */
  private renderStream(v: View, o: { waiting: boolean; stoppedEarly: boolean; show: boolean }): void {
    setHidden(this.waitCard, !(o.waiting || o.stoppedEarly))
    if (o.waiting) {
      const copy = waitingCopy(v.stream, v.streamStalled)
      setData(this.waitCard, 'kind', 'wait')
      setText(this.waitTitle, copy.title)
      setText(this.waitDetail, this.waitPaused ? WAIT_PAUSED : '')
      setHidden(this.waitDetail, !this.waitPaused)
      setHidden(this.waitMoon, false)
      // A paused wait (and a story that goes on only later) holds still.
      setData(this.waitMoon, 'pulse', this.waitPaused || v.stream?.state === 'paused' ? '0' : '1')
      setHidden(this.waitResumeBtn, !this.waitPaused)
      setHidden(this.endHereBtn, !copy.endHere)
      setHidden(this.waitRow, this.waitResumeBtn.hidden && this.endHereBtn.hidden)
    } else if (o.stoppedEarly) {
      setData(this.waitCard, 'kind', 'note')
      setText(this.waitTitle, STOPPED_EARLY)
      setHidden(this.waitDetail, true)
      setHidden(this.waitMoon, true)
      setHidden(this.waitRow, true)
    }
    const part = o.show ? partLabel(v.sceneIndex, v.stream) : null
    setHidden(this.partChip, part === null)
    if (part !== null) setText(this.partChip, part)
  }

  private render(): void {
    if (this.destroyed) return
    const v = this.view
    const errored = this.errorMessage !== null
    const panel = this.panelOpen && !errored
    const start = !panel && !errored && (v.needsGesture || v.loadingActive)
    const dimmed = !panel && !errored && !start && v.dimmed
    const isHold = v.phase === 'holding' || v.phase === 'question'
    // The dock's own play/pause is the one wherever the dock can show (any tap brings it up,
    // as in the app's player): the corner ❚❚ and the paused row of big buttons are only for
    // a story with no length to seek through yet.
    const dockOwnsPause = this.dockOwnsPause()
    // While the seek bar is dragged the pictures-only overlay steps aside: the
    // whole point of the drag is to see the frame it lands on.
    const pausedStill = !panel && !errored && !start && v.phase === 'paused' && !v.scrubbing
    const overlay = pausedStill && !dockOwnsPause
    const ended = !panel && !errored && !start && v.phase === 'ended'

    // Only on a change: the recorder and any MutationObserver on the root see
    // one record per phase change instead of sixty a second.
    setData(this.root, 'phase', v.phase)
    setData(this.root, 'hold', v.holdKind ?? '')

    // A hold with no ▶: `question` shows the `?` bubble (not "continue"), a
    // `tap_gate` shows the hand (ADDENDUM §5.3).
    const live = !panel && !errored && !start && !dimmed
    const showNext = live && isHold && v.holdKind === 'scene_end'
    const showQuestion = live && isHold && v.holdKind === 'question'
    const showGate = live && isHold && v.holdKind === 'tap_gate'
    const showAgain = live && (showNext || showQuestion) && v.canReplayLine
    // A story still being made: waiting at the end of the ready parts, or one that stopped early.
    const waiting = live && v.phase === 'waiting'
    const stoppedEarly = ended && v.stream?.state === 'failed' && v.stoppedShort
    // During the wait ❚❚ stays; once pressed, the card's "Keep going" takes its place.
    const showPause = live && !dockOwnsPause && (v.phase === 'playing' || isHold || (waiting && !this.waitPaused))

    setHidden(this.nextBtn, !(showNext || ended))
    setAttr(this.nextBtn, 'aria-label', ended ? 'Again!' : 'Next')
    setHidden(this.questionBtn, !showQuestion)
    setHidden(this.gateBtn, !showGate)
    setHidden(this.againBtn, !showAgain)
    setHidden(this.pauseBtn, !showPause)
    setHidden(this.actions, this.nextBtn.hidden && this.againBtn.hidden && (this.questionBtn.hidden || this.questionBtn.parentElement !== this.actions))

    setHidden(this.overlay, !overlay)
    setHidden(this.panel, !panel)
    setHidden(this.startScreen, !start)
    setHidden(this.dim, !dimmed)
    setHidden(this.errorScreen, !errored)

    // The gear is never a visible affordance while the picture is moving.
    setHidden(this.gearBtn, errored || start)
    // Close is always there in the kid player (start screen and error page too); the grown-up
    // panel has its own Exit, and an editor preview has no page to leave.
    setHidden(this.closeBtn, this.uiMode !== 'kid' || panel)
    setData(this.gearBtn, 'visible', this.uiMode === 'preview' || overlay || ended || isHold || waiting ? '1' : '0')

    this.renderStream(v, { waiting, stoppedEarly, show: !panel && !errored && !start && !dimmed && (isHold || waiting || pausedStill) })

    // Tap-anywhere is live at a hold (= next), and wherever the dock can show it is
    // play/pause, as in the app's player (`stageTap`). It is dead under a sheet, and — for a
    // story with no length yet, where the runner's policy still rules — dead while paused,
    // so only the overlay's buttons respond there.
    const tapLive = live && (dockOwnsPause || !pausedStill)
    setHidden(this.tapzone, !tapLive)
    const pe = tapLive ? 'auto' : 'none'
    if (this.tapzone.style.pointerEvents !== pe) this.tapzone.style.pointerEvents = pe

    if (showQuestion && v.holdLabel) {
      setAttr(this.questionBtn, 'aria-label', `${v.holdLabel} — tap when you are ready`)
    }
    if (showGate) setAttr(this.gateBtn, 'aria-label', v.holdLabel ?? 'Tap!')

    if (this.panelPrev && this.panelPrev.disabled !== v.sceneIndex <= 0) this.panelPrev.disabled = v.sceneIndex <= 0
    const lastScene = v.sceneIndex >= v.sceneCount - 1
    if (this.panelSkip && this.panelSkip.disabled !== lastScene) this.panelSkip.disabled = lastScene

    if (start) {
      const pct = Math.round(clamp(v.loadingProgress, 0, 1) * 100)
      const width = `${pct}%`
      if (this.startBarFill.style.width !== width) this.startBarFill.style.width = width
      const ready = !v.loadingActive || pct >= 100
      setHidden(this.startBtn, !ready)
      setHidden(this.startBar, ready)
      const label = ready ? 'Tap to start' : 'Getting the story ready…'
      if (this.startLabel.textContent !== label) this.startLabel.textContent = label
    }

    if (errored && this.errorText.textContent !== this.errorMessage) this.errorText.textContent = this.errorMessage

    const announce = waiting || stoppedEarly
      ? this.waitTitle.textContent
      : v.holdKind === 'tap_gate' || v.holdKind === 'question' ? v.holdLabel : null
    if (announce && this.live.textContent !== announce) this.live.textContent = announce

    const available = !panel && !errored && !start && !dimmed
    this.lastAvailable = available
    this.controls.setState({
      phase: v.phase,
      storyT: v.storyT,
      storyDuration: v.storyDuration,
      sceneStarts: v.sceneStarts,
      scrubbing: v.scrubbing,
      needsGesture: v.needsGesture,
      available,
      keysEnabled: !panel && !errored,
      reducedMotion: this.prefs.reducedMotion,
      lowDistraction: this.prefs.lowDistraction,
      subtitles: this.prefs.subtitles,
      rate: this.prefs.rate,
    })

    this.relayout(false, available)
    this.syncPanel()
  }

  // -- HudPort (the runner's side) ------------------------------------------

  setState(s: PlayerUiState): void {
    if (this.destroyed) return
    this.view = viewOf(s)
    // The part arrived (it lands paused, under the ordinary pause overlay) or the wait was left.
    if (this.view.phase !== 'waiting') this.waitPaused = false
    // The runner is the source of truth for the three prefs it owns.
    this.prefs = {
      ...this.prefs,
      mode: s.mode,
      questions: s.questions,
      subtitles: s.subtitles,
      reducedMotion: s.reducedMotion,
      lowDistraction: s.lowDistraction ?? this.prefs.lowDistraction,
      // The speed too, once the player publishes it (a hand-built state may not).
      rate: s.rate !== undefined ? clampRate(s.rate) : this.prefs.rate,
    }
    const key = `${this.view.phase}|${this.view.holdKind ?? ''}|${this.view.needsGesture}|${this.view.loadingActive}|${this.prefs.reducedMotion}`
    // Measure on a phase change, and once more when the renderer's canvas
    // appears; after that the ResizeObserver takes over. (Looking for the
    // canvas is a query, not a layout: a headless or canvas-less player no
    // longer forces a layout on every frame.)
    const canvasAppeared = this.autoMeasure && this.observedCanvas === null && this.host.querySelector('canvas') !== null
    if (key !== this.lastPhaseKey || canvasAppeared) {
      this.lastPhaseKey = key
      this.measure()
      this.applySizes()
    }
    this.render()
  }

  onTap(cb: (source: TapSource) => void): void {
    this.tapCbs.push(cb)
  }

  onControl(cb: (control: PlayerControl) => void): void {
    this.controlCbs.push(cb)
  }

  /** Seek bar and seek hotkeys, in story seconds. Never a tap (`controls.ts`). */
  onSeek(cb: (req: SeekRequest) => void): void {
    this.seekCbs.push(cb)
  }

  // -- HudHandle (the mount's side) -----------------------------------------

  onPrefs(cb: (patch: Partial<HudPrefs>) => void): void {
    this.prefsCbs.push(cb)
  }

  setStage(metrics: Partial<StageMetrics>): void {
    if (metrics.scale !== undefined && Number.isFinite(metrics.scale) && metrics.scale > 0) {
      this.stage.scale = metrics.scale
      this.sizes = hudSizes(metrics.scale, { pulse: !this.prefs.reducedMotion })
    }
    if (metrics.rect !== undefined) this.stage.rect = metrics.rect
    this.hostCache = null
    this.applySizes()
    this.render()
  }

  /** Stage-percent anchors for the `?` and the tap-gate hand (bubbles.ts). */
  setAnchors(a: { question?: readonly [number, number] | null; gate?: readonly [number, number] | null }): void {
    if (a.question !== undefined) this.questionPoint = a.question
    if (a.gate !== undefined) this.gatePoint = a.gate
    this.render()
  }

  setPrefs(prefs: Partial<HudPrefs>): void {
    const motionChanged = prefs.reducedMotion !== undefined && prefs.reducedMotion !== this.prefs.reducedMotion
    this.prefs = { ...this.prefs, ...prefs }
    this.prefs.rate = clampRate(this.prefs.rate)
    // The pulse itself is CSS (`[data-reduced-motion]`), but the gap it eats
    // is layout: with no pulse the row keeps the plain 24 px of §8.6.
    if (motionChanged) {
      this.sizes = hudSizes(this.stage.scale, { pulse: !this.prefs.reducedMotion })
      this.applySizes()
    }
    this.syncPanel()
    this.render()
  }

  getStage(): StageMetrics {
    return { scale: this.stage.scale, rect: this.stage.rect ? { ...this.stage.rect } : null }
  }

  getPrefs(): HudPrefs {
    return { ...this.prefs }
  }

  showError(message: string | null): void {
    this.errorMessage = message
    this.render()
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.cancelPress()
    // The mark's two timers would otherwise write to a root that is already gone.
    if (this.flashOn !== null) clearTimeout(this.flashOn)
    if (this.flashOff !== null) clearTimeout(this.flashOff)
    // Before the root goes: its document-level key listener must not outlive
    // the player, or a second mount would answer every key twice.
    this.controls.destroy()
    for (const d of this.disposers.splice(0)) d()
    this.host.style.removeProperty('--sp-ctl-over')
    this.host.style.removeProperty('--sp-ctl-below')
    this.mat.remove()
    this.glow.remove()
    this.root.remove()
  }
}

/**
 * Mount the HUD into `host` (the player's positioned container), then hand it
 * to `createPlayer` as `opts.hud`. The player wires `onTap`/`onControl`; the
 * mount wires `onPrefs`.
 */
export function createHud(host: HTMLElement, opts: HudOptions = {}): HudHandle {
  return new Hud(host, opts)
}

/** The prefs `createPlayer` understands, split out of a HUD patch. */
export function splitPrefs(patch: Partial<HudPrefs>): {
  runner: { mode?: PlayerMode; questions?: boolean; autoAnswer?: boolean; subtitles?: boolean }
  audio: { volume?: number; muted?: boolean }
  /** Playback speed — the player's (`setPrefs({ rate })`: clock and voice together). */
  rate: number | undefined
  reducedMotion: boolean | undefined
  lowDistraction: boolean | undefined
} {
  const runner: { mode?: PlayerMode; questions?: boolean; autoAnswer?: boolean; subtitles?: boolean } = {}
  if (patch.mode !== undefined) runner.mode = patch.mode
  if (patch.questions !== undefined) runner.questions = patch.questions
  if (patch.autoAnswer !== undefined) runner.autoAnswer = patch.autoAnswer
  if (patch.subtitles !== undefined) runner.subtitles = patch.subtitles
  const audio: { volume?: number; muted?: boolean } = {}
  if (patch.volume !== undefined) audio.volume = patch.volume
  if (patch.muted !== undefined) audio.muted = patch.muted
  return { runner, audio, rate: patch.rate, reducedMotion: patch.reducedMotion, lowDistraction: patch.lowDistraction }
}
